/**
 * CR-6160: live courier tracking panel for the ASN detail modal.
 *
 * Extracted from `ASNDetailModal` (CR-7194) to keep that file under the 500-line
 * cap. Presentational — the subscription decision and the refresh call are both
 * passed in.
 *
 * Two states, not one: a subscribed customer gets the full checkpoint timeline,
 * and everyone else gets a premium hint rather than an empty panel, so the
 * absence of tracking never reads as "tracking is broken".
 */
import { Truck, RefreshCw, MapPin } from 'lucide-react';
import type { ASNData } from './asnModalTypes';

// CR-6160: courier delivery-status badge styling (mirrors the backend
// COURIER_DELIVERY_STATUS_CHOICES / DeliveryStatus vocabulary).
const COURIER_STATUS_STYLES: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  in_transit: { label: 'In Transit', className: 'bg-blue-100 text-blue-700' },
  out_for_delivery: { label: 'Out for Delivery', className: 'bg-amber-100 text-amber-700' },
  delivered: { label: 'Delivered', className: 'bg-green-100 text-green-700' },
  exception: { label: 'Exception', className: 'bg-red-100 text-red-700' },
  unknown: { label: 'Unknown', className: 'bg-gray-100 text-gray-500' },
};

interface ASNTrackingPanelProps {
  asnData: ASNData;
  /** True when the tenant subscribes to the live-tracking module. */
  trackingEnabled: boolean;
  refreshingTracking: boolean;
  onRefresh: () => void;
  formatDate: (value?: string) => string;
}

export function ASNTrackingPanel({
  asnData, trackingEnabled, refreshingTracking, onRefresh, formatDate,
}: ASNTrackingPanelProps) {
  if (!trackingEnabled) {
    return (
      <div className="px-6 py-3 border-b bg-amber-50/50">
        <p className="text-xs text-amber-800 flex items-center gap-2">
          <Truck className="h-3.5 w-3.5" />
          Live courier tracking is a premium add-on — contact us to enable it.
        </p>
      </div>
    );
  }

  const status = asnData.courier_delivery_status
    ? (COURIER_STATUS_STYLES[asnData.courier_delivery_status] || COURIER_STATUS_STYLES['unknown'])
    : null;
  const hasRoute = !!(
    asnData.tracking_origin || asnData.tracking_destination || asnData.tracking_estimated_delivery
  );
  const checkpoints = asnData.tracking_checkpoints ?? [];

  return (
    <div className="px-6 py-4 border-b">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Live Courier Tracking</h3>
          {status && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${status.className}`}>
              {status.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {asnData.tracking_last_synced_at && (
            <span className="text-xs text-muted-foreground">
              Last synced {formatDate(asnData.tracking_last_synced_at)}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={refreshingTracking}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded border hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshingTracking ? 'animate-spin' : ''}`} />
            Refresh tracking
          </button>
        </div>
      </div>
      {hasRoute && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground mb-3">
          {asnData.tracking_origin && (
            <span><MapPin className="h-3 w-3 inline mr-1" />From {asnData.tracking_origin}</span>
          )}
          {asnData.tracking_destination && (
            <span><MapPin className="h-3 w-3 inline mr-1" />To {asnData.tracking_destination}</span>
          )}
          {asnData.tracking_estimated_delivery && (
            <span>Est. delivery {asnData.tracking_estimated_delivery}</span>
          )}
        </div>
      )}
      {checkpoints.length > 0 ? (
        <ol className="space-y-2 border-l-2 border-muted pl-4">
          {checkpoints.map((cp, i) => (
            <li key={i} className="text-xs relative">
              <span className="absolute -left-[21px] top-1 w-2 h-2 rounded-full bg-primary" />
              <span className="text-muted-foreground">{cp.timestamp || ''}</span>
              {cp.location && <span className="font-medium"> · {cp.location}</span>}
              {cp.message && <p className="text-muted-foreground">{cp.message}</p>}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-xs text-muted-foreground">
          No tracking checkpoints yet
          {asnData.tracking_number ? '' : ' — add a tracking number to enable live tracking'}.
        </p>
      )}
    </div>
  );
}
